import { Select, Space } from 'antd';
import NetworkIcon from 'components/NetworkIcon/NetworkIcon';
import React, { useState, useMemo, useEffect } from 'react';
import NetworkSwitch from 'components/switches/NetworkSwitch';
import useNetworkAccounts from 'store/redux/hooks/useNetworkAccounts';
import { LoadingOutlined } from '@ant-design/icons';
import debounce from 'common/debounce';

const NetworkAccountSelect = (props) => {
  const {
    value = [],
    onChange,
    network = 'all',
    multiple = true,
    disableNetworksSelector = false,
    disableSelectAll = false,
    label = 'Type To Search',
    placeholder = 'Select Accounts',
    networkSwitch = true,
    preload = 20,
    ...rest
  } = props;

  const [selectedNetwork, setSelectedNetwork] = useState();
  const [scrollPosition, setScrollPosition] = useState(0);
  const [search, setSearch] = useState();
  const [load, setLoad] = useState(preload);

  const { accounts, isLoading } = useNetworkAccounts({
    network: selectedNetwork === 'all' ? undefined : selectedNetwork,
    filters: {
      name: search,
      // network_account_ids: [],
    },
    per_page: load,
  });

  const normilizedValue = useMemo(() => {
    return (value || [])?.map((v) => Number(v));
  }, [value]);

  const handleSearch = debounce((searchValue) => {
    setSearch(searchValue);
  }, 500);

  const onSearch = (value) => {
    handleSearch(value);
  };

  useEffect(() => {
    if (scrollPosition > 90 && !(load > accounts.length)) {
      setTimeout(() => {
        setLoad(load + 50);
      }, 200);
    }
  }, [scrollPosition]);

  const [open, setOpen] = useState(false);

  const handleChange = (value) => {
    onChange?.(value);
  };

  useEffect(() => {
    setSelectedNetwork(network);
  }, [network]);

  const handleNetworksChange = (network) => {
    setSelectedNetwork(network);
  };

  const options = useMemo(() => {
    return accounts.filter((acc) => selectedNetwork === 'all' || acc.network === selectedNetwork);
  }, [accounts, selectedNetwork, search]);

  const selectAllChanged = (e) => {
    if (e.target.checked) {
      onChange?.(options.map((opt) => opt.id));
    } else {
      onChange?.([]);
    }
  };

  const allSelected = options?.length === value?.length;

  return (
    <Select
      mode={multiple && 'multiple'}
      style={{ width: '100%' }}
      open={open}
      placeholder={open ? label : placeholder}
      maxTagCount='responsive'
      onChange={handleChange}
      onSearch={onSearch}
      value={normilizedValue || []}
      allowClear
      onDropdownVisibleChange={() => setOpen(!open)}
      onPopupScroll={(e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const scrollPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;
        if (scrollPercentage > 90) {
          setScrollPosition(scrollPercentage);
        }
      }}
      dropdownRender={(menu) => (
        <>
          {networkSwitch && (
            <NetworkSwitch
              allSelected={allSelected}
              selectedNetwork={selectedNetwork}
              handleNetworksChange={handleNetworksChange}
              disableNetworksSelector={disableNetworksSelector}
              options={options}
              selectAllChanged={selectAllChanged}
              disableSelectAll={disableSelectAll}
            />
          )}
          <div className='transition-all duration-300 '>
            {menu}
            {isLoading && accounts.length ? (
              <div className='w-full m-auto text-center'>
                <LoadingOutlined className='m-auto transition-all duration-300' />
              </div>
            ) : null}
          </div>
        </>
      )}
      optionFilterProp='label'
      {...rest}
    >
      {options.map((opt) => (
        <Select.Option value={opt.id} label={opt.name} key={opt.id}>
          <Space>
            <span role='img' aria-label={opt.name}>
              <NetworkIcon network={opt.network} size={15} />
            </span>
            {opt.name}
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

export default NetworkAccountSelect;
